import { useCallback, useRef } from 'react'

/**
 * Hook for playing check-in success sound
 * Uses Web Audio API to generate a simple beep sound
 * Per 2026/02/04 spec update: Sound notification on successful check-in
 */
export const useCheckinSound = () => {
	const audioContextRef = useRef<AudioContext | null>(null)

	const playSuccessSound = useCallback(() => {
		try {
			if (!audioContextRef.current) {
				audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)()
			}

			const ctx = audioContextRef.current

			const playBeep = () => {
				const oscillator = ctx.createOscillator()
				const gainNode = ctx.createGain()

				oscillator.connect(gainNode)
				gainNode.connect(ctx.destination)

				// Configure a pleasant success beep
				oscillator.frequency.value = 880 // A5 note
				oscillator.type = 'sine'

				// Quick fade in/out for softer sound
				gainNode.gain.setValueAtTime(0, ctx.currentTime)
				gainNode.gain.linearRampToValueAtTime(0.3, ctx.currentTime + 0.02)
				gainNode.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.15)

				oscillator.start(ctx.currentTime)
				oscillator.stop(ctx.currentTime + 0.15)
			}

			if (ctx.state === 'suspended') {
				void ctx.resume().then(playBeep)
				return
			}

			playBeep()
		} catch (error) {
			console.warn('Failed to play check-in sound:', error)
		}
	}, [])

	const playAlreadyCheckedInSound = useCallback(() => {
		try {
			if (!audioContextRef.current) {
				audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)()
			}

			const ctx = audioContextRef.current

			const playDoubleBeep = () => {
				const createBeep = (frequency: number, startOffset: number, duration: number) => {
					const oscillator = ctx.createOscillator()
					const gainNode = ctx.createGain()

					oscillator.connect(gainNode)
					gainNode.connect(ctx.destination)

					oscillator.frequency.value = frequency
					oscillator.type = 'square'

					const startAt = ctx.currentTime + startOffset
					gainNode.gain.setValueAtTime(0, startAt)
					gainNode.gain.linearRampToValueAtTime(0.22, startAt + 0.01)
					gainNode.gain.linearRampToValueAtTime(0, startAt + duration)

					oscillator.start(startAt)
					oscillator.stop(startAt + duration)
				}

				createBeep(440, 0, 0.12)
				createBeep(440, 0.18, 0.12)
			}

			if (ctx.state === 'suspended') {
				void ctx.resume().then(playDoubleBeep)
				return
			}

			playDoubleBeep()
		} catch (error) {
			console.warn('Failed to play duplicate-check-in sound:', error)
		}
	}, [])

	return { playSuccessSound, playAlreadyCheckedInSound }
}

export default useCheckinSound
